<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-task-types">
        <i class="far fa-tags"></i> Task Types
      </Tab>
      <Tab tabid="tab-resource">
        <i class="far fa-code-branch"></i> Resource Tree
      </Tab>
      <Tab tabid="tab-task-settings">
        <i class="far fa-cogs"></i> Task Settings
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-task-types">
        <DataTable ref="taskTypeDataTable" :columns="taskTypeColumnList" defaultColumnOrderDir="ASC" :defaultColumnOrderIndex="0" :url="taskTypeDataURL" :displayTools="true">
          <template v-slot:title>
            <ContentTitle>Task Type List</ContentTitle>
            <ContentSubTitle>List of task types that specify the rules for tasks</ContentSubTitle>
          </template>
          <template v-slot:tools>
            <ActionButton type="new" v-if="userHasCreatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)" @click="addTaskType"> Add Task Type</ActionButton>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                {{row.data.name}}
              </td>
              <td>
                {{row.data.description}}
              </td>
              <td>
                <IconBoolean :value="row.data.isActive" />
              </td>
              <td>
                <ActionDropMenu :tableAction="true" :forceDropdown="true">
                  <DropdownMenuItem @click="editTaskType(row.data)" icon="fa-pencil" v-if="userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)">Edit Task Type</DropdownMenuItem>
                  <DropdownMenuItem @click="setTaskTypeActive(row.data, false)" icon="fa-times" v-if="row.data.isActive && userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)">Make Inactive</DropdownMenuItem>
                  <DropdownMenuItem @click="setTaskTypeActive(row.data, true)" icon="fa-check" v-if="!row.data.isActive && userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)">Make Active</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
          </template>
        </DataTable>

      </TabPage>

      <TabPage tabid="tab-resource">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="!resourceTree.editing" class="float-right">
              <DropdownMenuItem v-if="userHasCreatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)" @click="newResourceGroup" icon="fa-plus">New Resource Group</DropdownMenuItem>
              <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)" @click="resourceTree.editing = true" icon="fa-pencil">Edit Group Order</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Resource Tree</ContentTitle>
          </div>
        </div>
        <FormGroup :labelColumnSize="6" :form="resourceTree.form">
          <StaticDataTable class="static-table-list production-tree-list" :editing="resourceTree.editing" :hover="true" :striped="false" @sortUpdated="resourceTree.sortOrder = $event" :key="resourceTree.updateKey">
            <template v-slot:header>
              <tr>
                <th v-if="resourceTree.editing"></th>
                <th>Name</th>
                <th>Type</th>
                <th></th>
                <th class="text-center">Active</th>
                <th v-if="!resourceTree.editing">View</th>
                <th v-if="!resourceTree.editing">Actions</th>
              </tr>
            </template>
            <template v-slot:body>
              <template v-for="(resourceGroup, groupIndex) in resourceTree.resourceGroupList">
                <tr v-if="groupIndex > 0 && !resourceTree.editing" class="order-item-spacer" :key="`item-${groupIndex}-spacer`">
                  <td colspan="5"> </td>
                </tr>

                <tr :key="resourceGroup.id" :data-sort-id="resourceGroup.id" class="static-table-sortable production-tree-row" :style="getTreeGroupRowStyles(resourceGroup)">
                  <td v-if="resourceTree.editing" width="16px">
                    <div class="static-datatable-row-draggable"><i class="fal fa-bars"></i> </div>
                  </td>
                  <td class="font-weight-bold">
                    <LinkResourceGroup :id="resourceGroup.id" :name="`Resource Group - ${resourceGroup.name}`" :newTab="false" />
                  </td>
                  <td>{{resourceGroup.type | formatLookupName(ResourceGroupTypeLookup)}}</td>
                  <td> </td>
                  <td class="text-center">
                    <IconBoolean :value="resourceGroup.isActive" :displayFalse="true" />
                  </td>
                  <td> </td>
                  <td>
                    <ActionDropMenu :tableAction="true" :forceDropdown="true">
                      <DropdownMenuItem v-if="userHasCreatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)" @click="newResource(resourceGroup.id)" icon="fa-plus">Add Resource</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
                <template v-if="!resourceTree.editing">
                  <tr v-for="resource in resourceGroup.resources" class="production-tree-row" :key="`group-${resourceGroup.id}-resource-${resource.id}`" :style="getTreeGroupRowStyles(resourceGroup)">
                    <td>
                      <i class="fas fa-caret-right mr-2 ml-3"></i>
                      <LinkResource :id="resource.id" :name="resource.name" :newTab="false" />
                    </td>
                    <td>
                      {{resource.type | formatLookupName(ResourceTypeLookup)}}
                    </td>
                    <td>
                      
                    </td>
                    <td class="text-center">
                      <IconBoolean :value="resource.isActive" :displayFalse="true" />
                    </td>
                    <td>
                    </td>
                    <td> </td>
                  </tr>
                  
                </template>
              </template>
            </template>
          </StaticDataTable>

          <FormGroup>
            <div v-if="resourceTree.editing">
              <FormHorizontalRule />
              <FormSubmit label="Update" :submitting="resourceTree.submitting" @cancelled="cancelResourceTreeEditing" @submitted="submitResourceTreeEditing"></FormSubmit>
            </div>
          </FormGroup>
        </FormGroup>
      </TabPage>

      <TabPage tabid="tab-task-settings">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig) && !taskSettings.editing" class="float-right">
              <DropdownMenuItem @click="taskSettings.editing = true" icon="fa-pencil">Edit Task Settings</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Task Settings</ContentTitle>
            <ContentSubTitle>General settings for the Tasks module</ContentSubTitle>
          </div>
        </div>
        <FormGroup :labelColumnSize="6" :form="taskSettings.form">
          <div class="row mt-2">
            <div class="col-lg-6">
              <FormAccountSetting label="Scan Despatch Task Items Enabled" helpDefault="False">
                <template v-slot:helpContent>
                  <p>If enabled, will allow scanning of despatch task item barcodes in the delivery / pickup task manager to automatically flag as despatched.</p>
                  <p>After scanning, all orders linked to scanned task items will be moved into completed state and will be automatically invoiced.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!taskSettings.editing" :value="getConfigSettingBoolean(taskSettings.settings, AccountSettingKeys.taskDespatchScanTaskItemsEnabled)" />
                  <FormCheckbox v-if="taskSettings.editing" :value="getConfigSettingBoolean(taskSettings.settings, AccountSettingKeys.taskDespatchScanTaskItemsEnabled)" @change="setConfigSetting(taskSettings.settings, AccountSettingKeys.taskDespatchScanTaskItemsEnabled, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Scan Despatch Task Items - Auto Complete Production" helpDefault="False">
                <template v-slot:helpContent>
                  <p>If enabled, will automatically complete production for any orders linked to scanned task items.</p>
                  <p>Requires task item scanning to be enabled.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!taskSettings.editing" :value="getConfigSettingBoolean(taskSettings.settings, AccountSettingKeys.taskDespatchScanForceProductionEnabled)" />
                  <FormCheckbox v-if="taskSettings.editing" :value="getConfigSettingBoolean(taskSettings.settings, AccountSettingKeys.taskDespatchScanForceProductionEnabled)" @change="setConfigSetting(taskSettings.settings, AccountSettingKeys.taskDespatchScanForceProductionEnabled, $event)" />
                </template>
              </FormAccountSetting>
            </div>
          </div>

          <FormSubmit v-if="taskSettings.editing" label="Update" :submitting="taskSettings.submitting" @submitted="submitTaskConfigSettings" @cancelled="cancelTaskConfigSettings" />
        </FormGroup>
      </TabPage>


    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import DataDisplayMixin from '@/mixins/dataDisplay'

import TaskTypeDialog from '@/modules/tasks/views/taskTypeDialog'
import ResourceGroupDialog from '@/modules/tasks/views/resourceGroupDialog'
import ResourceDialog from '@/modules/tasks/views/resourceDialog'

import { ResourceGroupTypeLookup, ResourceTypeLookup, AccountSettingKeys } from '@/store/types'

export default {
  name: 'TasksConfiguration',
  mixins: [form, DataDisplayMixin],
  components: {
    
  },
  data: () => {
    return {
      taskTypeDataURL: '/tasks/tasktypes?',
      taskTypeColumnList: [
        { title: "Name", data: "name" },
        { title: "Description", data: "description" },
        { title: "Active", data: "isActive" },
        { title: "Actions", data: "", sortable: false },
      ], 

      contactLookup: [],

      resourceTree: {
        editing: false,
        submitting: false,
        updateKey: 0,
        sortOrder: [],
        form: {},
        resourceGroupList: [],
        resourceList: []
      },

      taskSettings: {
        editing: false,
        submitting: false,
        form: {},
        settings: {}
      },
    }
  },
  created() {
    this.ResourceGroupTypeLookup = ResourceGroupTypeLookup
    this.ResourceTypeLookup = ResourceTypeLookup
    this.AccountSettingKeys = AccountSettingKeys
  },
  mounted() {
    this.requestData()
  },
  computed: {
    
  },
  methods: {
    reloadData() {
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.tasksService.getResourceGroupList(),
        this.$apiService.tasksService.getResourceList(),

        this.$apiService.tasksService.getConfigSettings(),

        this.$apiService.contactService.getContactList({ isActive: 1 }),
      ]))
      .then(results => {
        this.resourceTree.resourceGroupList = results[0].data
        this.resourceTree.resourceList = results[1].data

        this.resourceTree.sortOrder = this.resourceTree.resourceGroupList.map(group => group.id)

        this.$data.taskSettings.settings = results[2].data

        this.contactLookup = results[3].data.map(item => {
          return { value: item.id, name: item.name }
        })
      })
      .catch(err => this.handlePageLoadError(err))
    },


    getTreeGroupRowStyles(group) {
      return {
        'border-left': `4px solid ${group.highlightColour}`
      }
    },


    // Resource Tree
    newResourceGroup() {
      this.displayModal(ResourceGroupDialog, { 
        isCreateMode: true
      })
      .then(groupId => {
        if(groupId) {
          this.successToast('Resource Group Added')
          this.$router.push(`/tasks/resourcegroups/${groupId}`)
        }
      })
    },
    newResource(resourceGroupId = null) {
      this.displayModal(ResourceDialog, { 
        isCreateMode: true,
        resourceGroupId: resourceGroupId
      })
      .then(resourceId => {
        if(resourceId) {
          this.successToast('Resource Added')
          this.$router.push(`/tasks/resources/${resourceId}`)
        }
      })
    },

     cancelResourceTreeEditing() {
      this.resourceTree.editing = false
      this.resourceTree.submitting = false

      this.$nextTick(() => {
        // JB: This is hacky, but cannot seem to get the table data to reset after sortable()
        this.resourceTree.updateKey++
      })

      // Refresh the resource tree
      this.reloadData()
    },
    submitResourceTreeEditing() {
      this.resourceTree.submitting = true

      var resourceGroupBatchData = this.resourceTree.sortOrder.map((groupId, index) => {
        return {
          id: groupId,
          sortOrder: index
        }
      })

      this.$apiService.tasksService.updateResourceGroupBatch({ groups: resourceGroupBatchData })
      .then(response => {
        this.resourceTree.editing = false
        this.resourceTree.submitting = false
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update resource group order')                
      })
      .finally(() => {
        this.resourceTree.submitting = false
      })
    },


    // General Config Settings
    getConfigSetting(settingsData, settingKey, defaultValue = '') {
      if(settingsData[settingKey] !== undefined)
        return settingsData[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingsData, settingKey, defaultValue = false) {
      if(settingsData[settingKey] !== undefined)
        return this.parseBoolean(settingsData[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingsData, settingKey, defaultValue = 0) {
      if(settingsData[settingKey] !== undefined)
        return parseInt(settingsData[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingsData, settingKey, defaultValue = 0) {
      if(settingsData[settingKey] !== undefined)
        return this.parseDecimal(settingsData[settingKey])
      return defaultValue
    },
    setConfigSetting(settingsData, settingKey, value) {
      settingsData[settingKey] = value
    },

    submitTaskConfigSettings() {
      this.$data.taskSettings.submitting = true
      this.$apiService.tasksService.updateConfigSettings({ settings: this.$data.taskSettings.settings })
      .then(response => {
        this.$data.taskSettings.editing = false
        this.successToast('Task Config Settings updated')
      })
      .catch(err => {
        this.$data.taskSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.taskSettings.submitting = false
      })
    },
    cancelTaskConfigSettings() {
      this.$data.taskSettings.editing = false
      this.$apiService.tasksService.getConfigSettings()
      .then(response => {
        this.$data.taskSettings.settings = response.data
      })
    },

    addTaskType() {
      this.displayModal(TaskTypeDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Task Type Created')
          this.$refs.taskTypeDataTable.reload()
        }
      })
    },
    editTaskType(taskType) {
      this.displayModal(TaskTypeDialog, { 
        isCreateMode: false,
        id: taskType.id
      })
      .then((success) => {
        if(success) {
          this.successToast('Task Type Updated')
          this.$refs.taskTypeDataTable.reload()
        }
      })
    },
    setTaskTypeActive(taskType, isActive) {
      this.$apiService.tasksService.updateTaskType(taskType.id, { isActive: isActive })
      .then(response => {
        this.successToast('Task Type Updated')
        this.$refs.taskTypeDataTable.reload()
      })
    },
   

  }
}

</script>